import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import * as S from "./style";

const FAMILY_SITES = [
  { label: "YEOLMAECOMPANY", url: "https://yeolmaecompany.com/" },
  { label: "COLLECTOR'S ROOM", url: "https://collectorsroom.co.kr/" },
  { label: "YEOLMAEVENTURES", url: "https://ymventures.co.kr/" },
  { label: "BBUZZART", url: "https://bbuzzart.com/" },
];

interface MenuPosition {
  bottom: number;
  left: number;
  width: number;
}

interface FamilySiteProps {
  fullWidth?: boolean;
}

function FamilySite({ fullWidth = false }: FamilySiteProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;

    const onPointerDownOutside = (e: PointerEvent) => {
      const target = e.target as Node;
      const inside =
        !!wrapRef.current?.contains(target) ||
        !!menuRef.current?.contains(target);
      if (!inside) {
        setOpen(false);
      }
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDownOutside);
    }, 0);
    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener("pointerdown", onPointerDownOutside);
    };
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const menu =
    open && menuPos
      ? ReactDOM.createPortal(
          <S.PortalList
            ref={menuRef}
            style={{
              left: menuPos.left,
              width: menuPos.width,
              bottom: menuPos.bottom,
            }}
          >
            {FAMILY_SITES.map((site) => (
              <S.Item
                key={site.label}
                type="button"
                onClick={() => {
                  window.open(site.url, "_blank");
                  setOpen(false);
                }}
              >
                {site.label}
              </S.Item>
            ))}
          </S.PortalList>,
          document.body
        )
      : null;

  return (
    <>
      <S.Wrap ref={wrapRef} $fullWidth={fullWidth}>
        <S.Button
          type="button"
          $fullWidth={fullWidth}
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          <span>FAMILY SITE</span>
          {open ? <FaAngleUp /> : <FaAngleDown />}
        </S.Button>
      </S.Wrap>
      {menu}
    </>
  );
}

export default FamilySite;
